import React, { useCallback, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import {
  AlertTriangle,
  CheckCircle,
  Eye,
  Printer,
  RefreshCw,
  Trash2,
  X,
} from 'lucide-react';
import MainService from '../../services/MainService';
import { getApiBaseUrl } from '../../services/appSettings';

const formatDate = (dateString) => {
  if (!dateString) return '-';
  try {
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return dateString;
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch {
    return dateString;
  }
};

const formatMoney = (amount) => {
  if (amount === null || amount === undefined) return '0.00';
  return Number(amount).toFixed(2);
};

const yesNo = (v) => (v ? 'Yes' : 'No');

const isSuccess = (response) =>
  response === 'Success' || response === true || response === 'true';

/**
 * Client return preview modal.
 * mode = 'view'  → read-only preview
 * mode = 'edit'  → preview + delete / unapprove / remove & recalculate
 */
const ClientReturnPreview = ({ returnId, mode = 'view', onClose, onChanged }) => {
  const isEdit = mode === 'edit';
  const [clientReturn, setClientReturn] = useState(null);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedCrc, setSelectedCrc] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);

  const loadReturn = useCallback(async () => {
    if (!returnId) return;
    try {
      setLoading(true);
      setError('');
      const response = await MainService.GetClientReturnById(returnId);
      if (response?.Id) {
        setClientReturn(response);
      } else {
        setError('Failed to load return details');
        setClientReturn(null);
      }
    } catch (err) {
      console.error('Error loading client return:', err);
      setError('Failed to load return details');
      setClientReturn(null);
    } finally {
      setLoading(false);
    }
  }, [returnId]);

  useEffect(() => {
    loadReturn();
  }, [loadReturn]);

  useEffect(() => {
    if (!error) return undefined;
    const t = setTimeout(() => setError(''), 5000);
    return () => clearTimeout(t);
  }, [error]);

  useEffect(() => {
    if (!success) return undefined;
    const t = setTimeout(() => setSuccess(''), 3000);
    return () => clearTimeout(t);
  }, [success]);

  const openReport = async (apiFn, id, failMsg) => {
    try {
      setActionLoading(true);
      const response = await apiFn(id);
      if (response) {
        window.open(`${getApiBaseUrl()}ReportFiles/${response}`, '_blank');
      } else {
        setError(failMsg);
      }
    } catch (err) {
      console.error(failMsg, err);
      setError(failMsg);
    } finally {
      setActionLoading(false);
    }
  };

  const runConfirmedAction = async () => {
    if (!confirmAction) return;
    const { type, id } = confirmAction;
    try {
      setActionLoading(true);
      setError('');
      setSuccess('');
      let response;
      if (type === 'deleteReturn') {
        response = await MainService.DeleteClientReturn(id);
      } else if (type === 'removeCrc') {
        response = await MainService.RemoveCRCFromCR(id);
      } else if (type === 'removeOrder') {
        response = await MainService.RemoveCRCOrder(id);
      } else if (type === 'unapprove') {
        response = await MainService.UnapproveClientReturn(id);
      }

      if (isSuccess(response)) {
        setSuccess(
          type === 'deleteReturn'
            ? 'Return deleted successfully'
            : type === 'unapprove'
              ? 'Return unapproved successfully'
              : 'Removed and recalculated successfully'
        );
        setConfirmAction(null);
        setSelectedCrc(null);
        if (type === 'deleteReturn' || type === 'unapprove') {
          onChanged?.();
          if (type === 'deleteReturn') {
            onClose?.();
            return;
          }
        }
        await loadReturn();
        onChanged?.();
      } else {
        setError(response || 'Operation failed');
        setConfirmAction(null);
      }
    } catch (err) {
      console.error('Client return action error:', err);
      setError('Network error');
      setConfirmAction(null);
    } finally {
      setActionLoading(false);
    }
  };

  const clients = clientReturn?.ClientReturnClients || [];

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/50 p-4">
      <div className="flex max-h-[92vh] w-full max-w-6xl flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xl">
        <div className="flex shrink-0 items-center justify-between border-b border-gray-100 px-5 py-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              {isEdit ? 'Edit Return' : 'View Return'}
              {clientReturn?.RefNo ? ` — ${clientReturn.RefNo}` : ''}
            </h3>
            <p className="mt-0.5 text-xs text-gray-500">
              {isEdit ? 'Preview with delete & recalculate options' : 'Read-only preview'}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto p-5">
          {error && (
            <div className="mb-4 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              <span className="flex-1">{error}</span>
            </div>
          )}
          {success && (
            <div className="mb-4 flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-700">
              <CheckCircle className="h-4 w-4 shrink-0" />
              <span className="flex-1">{success}</span>
            </div>
          )}

          {loading || !clientReturn ? (
            <div className="flex items-center justify-center gap-2 py-16 text-gray-500">
              <RefreshCw className={`h-5 w-5 ${loading ? 'animate-spin' : ''}`} />
              <span>{loading ? 'Loading return…' : 'No return data'}</span>
            </div>
          ) : (
            <>
              <div className="mb-6 overflow-x-auto">
                <table className="w-full border border-gray-200 text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="border border-gray-200 px-3 py-2 text-left font-medium text-gray-600">Ref No</th>
                      <th className="border border-gray-200 px-3 py-2 text-left font-medium text-gray-600">Date</th>
                      <th className="border border-gray-200 px-3 py-2 text-left font-medium text-gray-600">Status</th>
                      <th className="border border-gray-200 px-3 py-2 text-right font-medium text-gray-600">Total Orders</th>
                      <th className="border border-gray-200 px-3 py-2 text-left font-medium text-gray-600">Employee</th>
                      <th className="border border-gray-200 px-3 py-2 text-left font-medium text-gray-600">Created By</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gray-200 px-3 py-2 font-medium text-gray-900">
                        {clientReturn.RefNo || '-'}
                      </td>
                      <td className="border border-gray-200 px-3 py-2 text-gray-700">
                        {formatDate(clientReturn.Date)}
                      </td>
                      <td className="border border-gray-200 px-3 py-2 text-gray-700">
                        {clientReturn.Status || '-'}
                      </td>
                      <td className="border border-gray-200 px-3 py-2 text-right tabular-nums text-gray-900">
                        {clientReturn.TotalOrders || 0}
                      </td>
                      <td className="border border-gray-200 px-3 py-2 text-gray-700">
                        {clientReturn.Employee?.Name || '-'}
                      </td>
                      <td className="border border-gray-200 px-3 py-2 text-gray-700">
                        {clientReturn.Createdby?.UserName || clientReturn.CreatedBy?.UserName || '-'}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <p className="mb-2 text-sm font-semibold text-gray-900">Details</p>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[900px] border border-gray-200 text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="border border-gray-200 px-3 py-2 text-left">#</th>
                      <th className="border border-gray-200 px-3 py-2 text-left">Ref No</th>
                      <th className="border border-gray-200 px-3 py-2 text-left">Client</th>
                      <th className="border border-gray-200 px-3 py-2 text-left">Mobile</th>
                      <th className="border border-gray-200 px-3 py-2 text-left">Client No</th>
                      <th className="border border-gray-200 px-3 py-2 text-right">Total Orders</th>
                      <th className="border border-gray-200 px-3 py-2 text-left">Status</th>
                      <th className="border border-gray-200 px-3 py-2 text-center">Options</th>
                    </tr>
                  </thead>
                  <tbody>
                    {clients.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="border border-gray-200 px-3 py-8 text-center text-gray-500">
                          No client lines
                        </td>
                      </tr>
                    ) : (
                      clients.map((crc, index) => (
                        <tr key={crc.Id || index} className="hover:bg-gray-50">
                          <td className="border border-gray-200 px-3 py-2">{index + 1}</td>
                          <td className="border border-gray-200 px-3 py-2">{crc.RefNo || '-'}</td>
                          <td className="border border-gray-200 px-3 py-2">{crc.Client?.Name || '-'}</td>
                          <td className="border border-gray-200 px-3 py-2">{crc.Client?.MobileNo1 || '-'}</td>
                          <td className="border border-gray-200 px-3 py-2">{crc.Client?.ClientNo || '-'}</td>
                          <td className="border border-gray-200 px-3 py-2 text-right tabular-nums">
                            {crc.TotalOrders || 0}
                          </td>
                          <td className="border border-gray-200 px-3 py-2">{crc.Status || '-'}</td>
                          <td className="border border-gray-200 px-3 py-2">
                            <div className="flex flex-wrap items-center justify-center gap-1.5">
                              <button
                                type="button"
                                onClick={() => setSelectedCrc(crc)}
                                className="inline-flex items-center gap-1 rounded-md border border-gray-200 px-2 py-1 text-xs text-gray-700 hover:bg-gray-50"
                              >
                                <Eye className="h-3.5 w-3.5" />
                                View Orders
                              </button>
                              {isEdit && (
                                <button
                                  type="button"
                                  onClick={() =>
                                    setConfirmAction({
                                      type: 'removeCrc',
                                      id: crc.Id,
                                      label: `Delete client line "${crc.Client?.Name || crc.RefNo}" and recalculate?`,
                                    })
                                  }
                                  className="inline-flex items-center gap-1 rounded-md border border-red-200 px-2 py-1 text-xs text-red-700 hover:bg-red-50"
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                  Delete & Recalculate
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>

        {clientReturn && (
          <div className="flex shrink-0 flex-wrap items-center justify-end gap-2 border-t border-gray-100 bg-gray-50 px-5 py-3">
            <button
              type="button"
              onClick={loadReturn}
              disabled={loading || actionLoading}
              className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <button
              type="button"
              onClick={() =>
                openReport(MainService.PrintClientReturn, clientReturn.Id, 'Failed to print return')
              }
              disabled={actionLoading}
              className="inline-flex items-center gap-1.5 rounded-lg bg-green-600 px-3 py-2 text-sm text-white hover:bg-green-700 disabled:opacity-50"
            >
              <Printer className="h-4 w-4" />
              Print
            </button>
            <button
              type="button"
              onClick={() =>
                openReport(
                  MainService.PrintClientReturnList,
                  clientReturn.Id,
                  'Failed to print return list'
                )
              }
              disabled={actionLoading}
              className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-2 text-sm text-white hover:bg-emerald-700 disabled:opacity-50"
            >
              <Printer className="h-4 w-4" />
              Print List
            </button>
            {isEdit && (
              <>
                <button
                  type="button"
                  onClick={() =>
                    setConfirmAction({
                      type: 'deleteReturn',
                      id: clientReturn.Id,
                      label: `Delete return "${clientReturn.RefNo}"?`,
                    })
                  }
                  disabled={actionLoading}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-red-600 px-3 py-2 text-sm text-white hover:bg-red-700 disabled:opacity-50"
                >
                  <Trash2 className="h-4 w-4" />
                  Delete
                </button>
                {clientReturn.Status === 'APPROVED' && (
                  <button
                    type="button"
                    onClick={() =>
                      setConfirmAction({
                        type: 'unapprove',
                        id: clientReturn.Id,
                        label: `Unapprove return "${clientReturn.RefNo}"?`,
                      })
                    }
                    disabled={actionLoading}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-amber-500 px-3 py-2 text-sm text-white hover:bg-amber-600 disabled:opacity-50"
                  >
                    UnApprove
                  </button>
                )}
              </>
            )}
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
            >
              Close
            </button>
          </div>
        )}
      </div>

      {selectedCrc && (
        <div className="fixed inset-0 z-[100000] flex items-center justify-center bg-black/40 p-4">
          <div className="flex max-h-[90vh] w-full max-w-5xl flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xl">
            <div className="flex items-center justify-between border-b border-gray-100 px-5 py-3">
              <h4 className="text-base font-semibold text-gray-900">
                Orders — {selectedCrc.Client?.Name || selectedCrc.RefNo || 'Client'}
              </h4>
              <button
                type="button"
                onClick={() => setSelectedCrc(null)}
                className="rounded-lg p-2 text-gray-400 hover:bg-gray-100"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="min-h-0 flex-1 overflow-auto p-4">
              <table className="w-full min-w-[1000px] border border-gray-200 text-xs">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="border border-gray-200 px-2 py-1.5 text-left">#</th>
                    <th className="border border-gray-200 px-2 py-1.5 text-left">Voucher No</th>
                    <th className="border border-gray-200 px-2 py-1.5 text-left">Delivery Date</th>
                    <th className="border border-gray-200 px-2 py-1.5 text-left">Customer</th>
                    <th className="border border-gray-200 px-2 py-1.5 text-left">Mobile</th>
                    <th className="border border-gray-200 px-2 py-1.5 text-right">Product</th>
                    <th className="border border-gray-200 px-2 py-1.5 text-right">Delivery</th>
                    <th className="border border-gray-200 px-2 py-1.5 text-right">VAT</th>
                    <th className="border border-gray-200 px-2 py-1.5 text-right">Client Money</th>
                    <th className="border border-gray-200 px-2 py-1.5 text-left">Pay Type</th>
                    <th className="border border-gray-200 px-2 py-1.5 text-center">Adv Charge</th>
                    <th className="border border-gray-200 px-2 py-1.5 text-center">Adv Price</th>
                    <th className="border border-gray-200 px-2 py-1.5 text-center">Free</th>
                    <th className="border border-gray-200 px-2 py-1.5 text-left">Status</th>
                    {isEdit && (
                      <th className="border border-gray-200 px-2 py-1.5 text-center">Options</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {(selectedCrc.ClientReturnClientOrders || []).map((row, index) => {
                    const order = row.Order || {};
                    return (
                      <tr key={row.Id || index}>
                        <td className="border border-gray-200 px-2 py-1.5">{index + 1}</td>
                        <td className="border border-gray-200 px-2 py-1.5">{order.VoucherNo || '-'}</td>
                        <td className="border border-gray-200 px-2 py-1.5">{formatDate(order.DeliveryDate)}</td>
                        <td className="border border-gray-200 px-2 py-1.5">{order.Customer?.Name || '-'}</td>
                        <td className="border border-gray-200 px-2 py-1.5">{order.Customer?.MobileNo1 || '-'}</td>
                        <td className="border border-gray-200 px-2 py-1.5 text-right">
                          {formatMoney(order.ProductPrice)}
                        </td>
                        <td className="border border-gray-200 px-2 py-1.5 text-right">
                          {formatMoney(order.DeliveryCharge)}
                        </td>
                        <td className="border border-gray-200 px-2 py-1.5 text-right">{formatMoney(order.TAX)}</td>
                        <td className="border border-gray-200 px-2 py-1.5 text-right">
                          {formatMoney(order.ClientAmount)}
                        </td>
                        <td className="border border-gray-200 px-2 py-1.5">{order.PayType || '-'}</td>
                        <td className="border border-gray-200 px-2 py-1.5 text-center">{yesNo(order.IsAdvancePay)}</td>
                        <td className="border border-gray-200 px-2 py-1.5 text-center">{yesNo(order.IsAdvancePrice)}</td>
                        <td className="border border-gray-200 px-2 py-1.5 text-center">{yesNo(order.IsFree)}</td>
                        <td className="border border-gray-200 px-2 py-1.5">{order.Status?.Name || '-'}</td>
                        {isEdit && (
                          <td className="border border-gray-200 px-2 py-1.5 text-center">
                            <button
                              type="button"
                              onClick={() =>
                                setConfirmAction({
                                  type: 'removeOrder',
                                  id: row.Id,
                                  label: `Remove order "${order.VoucherNo}" and recalculate?`,
                                })
                              }
                              className="rounded-md border border-red-200 px-2 py-1 text-[11px] text-red-700 hover:bg-red-50"
                            >
                              Remove & Recalculate
                            </button>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="flex justify-end border-t border-gray-100 px-5 py-3">
              <button
                type="button"
                onClick={() => setSelectedCrc(null)}
                className="rounded-lg border border-gray-200 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmAction && (
        <div className="fixed inset-0 z-[100001] flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-xl border border-gray-200 bg-white p-6 shadow-xl">
            <div className="mb-4 flex items-start gap-3">
              <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-500" />
              <p className="text-sm text-gray-700">{confirmAction.label}</p>
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirmAction(null)}
                disabled={actionLoading}
                className="rounded-lg border border-gray-200 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={runConfirmedAction}
                disabled={actionLoading}
                className="inline-flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm text-white hover:bg-red-700 disabled:opacity-50"
              >
                {actionLoading && <RefreshCw className="h-4 w-4 animate-spin" />}
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>,
    document.body
  );
};

export default ClientReturnPreview;
